import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Update page title and meta
    document.title = '聯絡我們 - Flan Cake 芙蘭蛋糕 | 客服專線與線上客服';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '聯絡芙蘭蛋糕，客服專線 0929-711-129，週一至週日 09:00-21:00。總部位於台中市，提供產品諮詢、訂單問題、門市資訊、加盟諮詢等服務。');
    }

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "聯絡 Flan Cake 芙蘭蛋糕",
      "description": "有任何問題或建議，歡迎與我們聯繫",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Flan Cake 芙蘭蛋糕",
        "telephone": "+886-929-711-129",
        "email": "eric0929711129@gmail.com",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "台中市",
          "addressCountry": "TW"
        }
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Flan Cake 芙蘭蛋糕，源自南美洲的經典甜點，絲滑細膩的口感與濃郁焦糖香氣。提供線上購物、門市洽詢與加盟服務，將百年傳承的南美甜蜜帶到台灣。芙蘭蛋糕專賣店提供經典原味、咖啡、抹茶等多種口味，台北、台中、高雄、台南門市籌備中。');
      }
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d66ong1i0m0fka7ok3ag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-lavender-50">
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="px-6 mb-24">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-800 mb-6">
              聯絡我們
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              有任何問題或建議，歡迎與我們聯繫
            </p>
          </div>
        </section>

        <section className="px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-3xl font-serif font-bold text-gray-800 mb-8">
                  聯絡資訊
                </h2>

                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-lavender-200 text-lavender-700 rounded-full flex-shrink-0">
                      <i className="ri-map-pin-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">總部地址</h3>
                      <p className="text-gray-700">台中市</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-lavender-200 text-lavender-700 rounded-full flex-shrink-0">
                      <i className="ri-phone-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">客服專線</h3>
                      <p className="text-gray-700">0929711129</p>
                      <p className="text-sm text-gray-600 mt-1">週一至週日 09:00 - 21:00</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-lavender-200 text-lavender-700 rounded-full flex-shrink-0">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">電子郵件</h3>
                      <p className="text-gray-700">eric0929711129@gmail.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-lavender-200 text-lavender-700 rounded-full flex-shrink-0">
                      <i className="ri-customer-service-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">線上客服</h3>
                      <p className="text-gray-700">點擊右下角圖示開始對話</p>
                    </div>
                  </div>
                </div>

                <div className="mt-12">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">追蹤我們</h3>
                  <div className="flex items-center gap-4">
                    <a
                      href="https://facebook.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-lavender-200 text-lavender-700 rounded-full hover:bg-lavender-600 hover:text-white transition-all cursor-pointer"
                    >
                      <i className="ri-facebook-fill text-xl"></i>
                    </a>
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-lavender-200 text-lavender-700 rounded-full hover:bg-lavender-600 hover:text-white transition-all cursor-pointer"
                    >
                      <i className="ri-instagram-line text-xl"></i>
                    </a>
                    <a
                      href="https://line.me"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 flex items-center justify-center bg-lavender-200 text-lavender-700 rounded-full hover:bg-lavender-600 hover:text-white transition-all cursor-pointer"
                    >
                      <i className="ri-line-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
                  <h2 className="text-3xl font-serif font-bold text-gray-800 mb-8">
                    留言給我們
                  </h2>

                  <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
                    <div className="space-y-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                          姓名 *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                          電子郵件 *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                          聯絡電話
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all"
                        />
                      </div>

                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                          主旨 *
                        </label>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all cursor-pointer"
                        >
                          <option value="">請選擇</option>
                          <option value="產品諮詢">產品諮詢</option>
                          <option value="訂單問題">訂單問題</option>
                          <option value="門市資訊">門市資訊</option>
                          <option value="加盟諮詢">加盟諮詢</option>
                          <option value="合作提案">合作提案</option>
                          <option value="其他">其他</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                          訊息內容 *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          maxLength={500}
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-lavender-600 focus:border-transparent outline-none transition-all resize-none"
                          placeholder="請詳細描述您的問題或需求..."
                        ></textarea>
                        <div className="text-xs text-gray-500 mt-1 text-right">
                          {formData.message.length}/500
                        </div>
                      </div>

                      {submitStatus === 'success' && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                          感謝您的留言！我們將盡快回覆您。
                        </div>
                      )}

                      {submitStatus === 'error' && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          提交失敗，請稍後再試。
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-lavender-600 text-white py-4 rounded-lg font-medium hover:bg-lavender-500 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                      >
                        {isSubmitting ? '提交中...' : '送出訊息'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
